import { useLayoutEffect } from 'react';
import {
  NavLink,
  useLocation,
  useNavigationType,
  type NavigationType,
  type NavLinkProps,
} from 'react-router-dom';

/**
 * Navigation state that asks `ScrollReset` to leave the page where it is.
 * Carried by `TabLink`; anything else that switches a view in place can pass it.
 */
export const KEEP_SCROLL = { keepScroll: true } as const;

/** Whether arriving at a new path this way starts the page at the top. */
export function resetsScrollOnArrival(
  type: `${NavigationType}`,
  at: { hash: string; state: unknown },
): boolean {
  if (type === 'POP' || at.hash) return false;
  return !(typeof at.state === 'object' && at.state !== null && 'keepScroll' in at.state);
}

/**
 * **A new page opens at the top.** The window is what scrolls (the main column
 * is not a scroll container), and the app runs under `BrowserRouter`, where
 * React Router's `<ScrollRestoration/>` is unavailable — so without this, a link
 * followed from far down one page opened the next page just as far down.
 *
 * Only a push or replace that changes the path resets. Back and Forward (POP)
 * are left to the browser's own restoration. A search-only change is the same
 * page re-filtered (the Analysis tab's `?analysis=`), a hash is a page's own
 * deep link (research.tsx scrolls to `#fact-…` itself), and `KEEP_SCROLL` marks
 * a tab switch, whose tab bar should stay under the pointer.
 *
 * A layout effect, so the reset lands before the new page paints and before
 * any page's own passive effect scrolls somewhere on purpose.
 */
export function ScrollReset() {
  const { pathname, hash, state } = useLocation();
  const type = useNavigationType();

  useLayoutEffect(() => {
    if (resetsScrollOnArrival(type, { hash, state })) window.scrollTo(0, 0);
    // Keyed on the path alone: the other inputs only decide whether this path
    // change resets, and a later state or hash change on the same path is not
    // a new page.
  }, [pathname]);

  return null;
}

/** A record's tab: a `NavLink` styled as one that keeps the scroll position. */
export function TabLink(props: Omit<NavLinkProps, 'className' | 'state'>) {
  return (
    <NavLink
      {...props}
      state={KEEP_SCROLL}
      className={({ isActive }) => (isActive ? 'tab active' : 'tab')}
    />
  );
}
